/* eslint-disable import/max-dependencies */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import dayjs from "dayjs";
import type { Dispatch } from "redux";
import { useParams } from "react-router";
import { Container, Box } from "@mui/material";
import { Loader } from "@fas/ui-core";
import { getErrors, type ErrorsState } from "@fas/ui-framework";
import { PAGE_TYPES } from "../../helpers/constants";
import type { PAGE_TYPES_OBJECT, PAGE_TYPES_TITLE_TYPE } from "../../helpers/constants";
import {
  getPageInfo,
  getPageList,
  getPageVersionByType,
  getPageCurrentVersionType,
  getPageVersions,
} from "../../selectors/page";
import {
  getIsPageLoading,
  getIsPageListLoading,
} from "../../selectors/loading";
import { getDocumentAliasMode } from "../../selectors/accountModules";
import {
  changePageName,
  changePageUrl,
  changePagePftTemplate,
  changePageTags,
  changePageTypeSaga,
  setPageVersion,
} from "../../actions/pageInfo";
import {
  getPageSaga,
  getPageVerisionPreviewSaga,
} from "../../actions/page";
import {
  getPagesListSaga,
  setPagesList,
} from "../../actions/pages";
import { pageBackupStore } from "../../pages/AppStoreWrapper/store";
import { setBackup, restoreBackup } from "../../actions/backup";
import PageInfo from "../../components/PageInfo/PageInfo";
import PageVersionActions from "../../components/PageVersionActions";
import type { State as PageStoreState } from "../../pages/AppStoreWrapper";
import type { State as PageInfoState } from "../../reducers/pageInfo";
import type { Page } from "../../components/PagesList/types/PagesList.types";
import type {
  Actions as PageActions,
  GetPageVerisionPreviewSagaAction,
} from "../../actions/page";
import {
  resetPageModifiers,
  setPageModifiers,
} from "../../actions/pageModifiers";
import type { Version, VersionType } from "../../reducers/pageVersions";
import { getPagesList } from "../../services/pagesApi";
import { getHashValue } from "../../services/jusCan";
import type { StateToProps, DispatchToProps, Props, Filters, Sorting } from "./types/PageInfo.type";

const mapStateToProps: (state: PageStoreState) => StateToProps = (state) => {
  const pageVersionType: VersionType = getPageCurrentVersionType(state);
  const pageVersion: Version = getPageVersionByType(state, pageVersionType);

  return {
    isPageLoading: getIsPageLoading(state),
    isTemplateListLoading: getIsPageListLoading(state), // FIXME: fix by optimized template api - MTU-51822
    pageInfo: getPageInfo(state),
    templateList: getPageList(state),
    errors: getErrors(state),
    documentAliasMode: getDocumentAliasMode(state),
    pageVersions: getPageVersions(state),
    pageVersionType,
    pageVersion,
  };
};

const mapDispatchToProps: (arg0: Dispatch<any>) => DispatchToProps = dispatch => ({
  ...bindActionCreators({
    onChangeType: changePageTypeSaga,
    onChangeName: changePageName,
    onChangeUrl: changePageUrl,
    onChangePagePftTemplate: changePagePftTemplate,
    onSetPageModifiers: setPageModifiers,
    onResetPageModifiers: resetPageModifiers,
    onGetPage: getPageSaga,
    onGetTemplateList: getPagesListSaga, // FIXME: fix by optimized template api - MTU-51822
    onSetTemplateList: setPagesList,
    onChangeTags: changePageTags,
    onGetPageVersionPreview: getPageVerisionPreviewSaga,
    onSetPageVersion: setPageVersion,
    onSetBackup: setBackup,
    onRestoreBackup: restoreBackup,
  }, dispatch),
});

function PageInfoContainer({
  isPageLoading,
  isTemplateListLoading,
  documentAliasMode,
  pageInfo,
  templateList,
  errors,
  pageVersionType,
  pageVersions,
  pageVersion,
  onChangeName,
  onChangeType,
  onChangeUrl,
  onChangePagePftTemplate,
  onGetPage,
  onGetTemplateList,
  onSetTemplateList,
  onChangeTags,
  onSetPageModifiers,
  onResetPageModifiers,
  onGetPageVersionPreview,
  onSetPageVersion,
  onSetBackup,
  onRestoreBackup,
}: Props) {
  const [inputPftTemplate, setInputPftTemplate] = useState("");
  const { id: pageId } = useParams<{ id?: string }>();

  const {
    name,
    pageType,
    versionId,
    tags,
    url,
    template,
  }: PageInfoState = pageInfo;
  const { templateName } = template;

  const pageTypesList: Array<PAGE_TYPES_TITLE_TYPE> = PAGE_TYPES.map((
    item: PAGE_TYPES_OBJECT
  ): PAGE_TYPES_TITLE_TYPE => item.pageTypeTitle);

  useEffect(() => {
    onSetBackup(pageBackupStore);
    if (pageId) onGetPage(pageId);
    return () => {
      onRestoreBackup();
    };
  }, [pageId, onGetPage]);

  useEffect(() => {
    if (pageType === "pft" && (inputPftTemplate === "" || inputPftTemplate === templateName)) {
      onSetTemplateList([]);
    }
    if (inputPftTemplate.length > 1 && inputPftTemplate !== templateName) {
      /* TODO: need refactor because there was using same  */
      getPagesList({
        page: 1,
        pageSize: 20,
        filters: {
          type: "template",
          name: inputPftTemplate,
        },
        sorting: {},
      }).then((response) => onSetTemplateList(response.data.data.pagesList));
    }
  }, [pageType, inputPftTemplate, templateName, onSetTemplateList, onGetTemplateList]);

  const handleInputChangePftTemplate: (value: string) => void = (value: string) => {
    setInputPftTemplate(value);
  };

  const handleChangePftTemplate: (value: string) => void = (value: string) => {
    const changedTemplate: Page | void = templateList.find((item: Page): boolean => item.name === value);
    onResetPageModifiers();
    if (changedTemplate && changedTemplate.documentId) {
      onChangePagePftTemplate(changedTemplate.documentId, value);
      // we set modifier from template without content
      onSetPageModifiers((changedTemplate.modifierCollection || []).map((item) => ({ ...item, content: "" })));
    }
  };

  const accountId: string = getHashValue();
  const versionPath = (versionId) ? `versionId=${versionId}` : "";
  const editPathName: string = (pageId) ? `/pages/edit/${pageId}?${versionPath}&hashId=${accountId}` : "";

  const handleChangePageVersion: (currentVersionType: VersionType) => void = (currentVersionType: VersionType) => {
    const { id: newVersionId }: Version = pageVersions.get(currentVersionType);
    onSetPageVersion(currentVersionType, newVersionId);
  };

  const pageVersionTypesValues: Array<{value: VersionType, title: string}> = [
    { value: "current" as VersionType, title: "Active version" },
    { value: "new" as VersionType, title: "Edit version" },
    { value: "previous" as VersionType, title: "Previous 1 version" },
    { value: "old" as VersionType, title: "Previous 2 version" },
  ].filter((item) => !!pageVersions.get(item.value).id);

  const pageVersionUpdatedAtFormatted: string = dayjs(pageVersion.updatedAt).format("DD-MM-YYYY hh:mm");

  return (
    <Container maxWidth="md">
      <Box data-testid="pages-general-info" width="100%">
        <Loader loading={isPageLoading}>
          <PageInfo
            documentAliasMode={documentAliasMode}
            pageTypesList={pageTypesList}
            pageType={pageType}
            pageId={pageId || null}
            name={name}
            url={url}
            template={template}
            templateList={templateList.map((item: Page): string => item.name)}
            isTemplateListLoading={isTemplateListLoading}
            tags={tags}
            errors={errors.toJS()}
            onChangeType={onChangeType}
            onChangeName={onChangeName}
            onChangeUrl={onChangeUrl}
            onChangePftTemplate={handleChangePftTemplate}
            onInputChangePftTemplate={handleInputChangePftTemplate}
            onChangeTags={onChangeTags}
          />
          {pageId && (
            <PageVersionActions
              pageVersionType={pageVersionType}
              pageVersionUpdatedAt={pageVersionUpdatedAtFormatted}
              pageVersionTypesValues={pageVersionTypesValues}
              onSetPageVersion={handleChangePageVersion}
              onGetPageVersionPreview={
                (): GetPageVerisionPreviewSagaAction => onGetPageVersionPreview(pageId || "", versionId || "")
              }
              editPathName={editPathName}
            />
          )}
        </Loader>
      </Box>
    </Container>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(PageInfoContainer);
